// Library Imports
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

// Local Imports
import { Alert, Button, TextField } from '../shared';
import assets from '../../assets';
import { validateEmail } from '../../utils/validator';
import { loginUserAPI, profileAPI } from '../../redux/features/loginUser/loginUserSlice';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { resendEmailAPI } from '../../redux/features/auth/authSlice';

const LoginForm = () => {
  const dispatch = useAppDispatch();
  const { loading, error } = useAppSelector(state => state?.currentUser);
  const authError = useAppSelector(state => state?.authUser?.error);
  const authLoading = useAppSelector(state => state?.authUser?.loading);
  interface SignInFormData {
    email: string;
    password: string;
    role?: 'admin';
  }
  // States
  const [signInFormData, setSignInFormData] = useState<SignInFormData>({
    email: '',
    password: '',
    role: 'admin'
  });
  const [isShowAlert, setIsShowAlert] = useState<boolean>(false);
  const [isVerifiedEmail, setIsVerifiedEmail] = useState<boolean>(false);
  const [isResendBtn, setIsResendBtn] = useState<boolean>(false);
  const [isRequestSend, setIsRequestSend] = useState<boolean>(false);
  const [emptyData, setEmptyData] = useState<any>([]);

  // Empty Check Fn
  const emptyCheck = () => {
    let isEmpty: any = {};
    Object.values(signInFormData).map((item, index) => {
      if (item === null || item === '') {
        isEmpty = {
          ...isEmpty,
          [Object.keys(signInFormData)[index]]: true
        };
      }
      return item;
    });
    if (!validateEmail(signInFormData.email) && signInFormData.email !== '') {
      isEmpty = {
        ...isEmpty,
        isEmailValid: true
      };
    }
    setEmptyData(isEmpty);

    return isEmpty ? (Object.keys(isEmpty).length === 0 ? false : true) : false;
  };

  // Handle FieldChange Fn
  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSignInFormData({ ...signInFormData, [name]: value });
    setEmptyData({
      ...emptyData,
      [name]: false,
      ...(name === 'email' ? { isEmailValid: false } : {})
    });
    setIsShowAlert(false);
  };

  // Handle SignIn Fn
  const handleSignIn = async () => {
    setIsShowAlert(false);
    if (!emptyCheck()) {
      const data = await dispatch(loginUserAPI(signInFormData));
      if (data.meta.requestStatus === 'rejected') {
        setIsResendBtn(data.payload?.includes('Your email is not verified. Please verify email.'));
        setIsShowAlert(true);
        return;
      }

      Object.keys(data?.payload?.data?.profile).length > 0 && dispatch(profileAPI());
      setSignInFormData({
        email: '',
        password: ''
      });
    }
  };

  // Handle Resend Email
  const handleResendEmail = async () => {
    setIsShowAlert(false);
    if (!validateEmail(signInFormData.email)) {
      setEmptyData({
        isEmailValid: true
      });
      return;
    }
    if (signInFormData.email === '') {
      setEmptyData({
        email: true
      });
      return;
    }
    const data = await dispatch(resendEmailAPI({ email: signInFormData.email }));
    if (data.meta.requestStatus === 'rejected') {
      setIsShowAlert(true);
      return;
    }
    setIsVerifiedEmail(false);
    setIsResendBtn(false);
    setIsRequestSend(true);
  };
  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (event.code === 'Enter' || event.code === 'NumpadEnter') {
        event.preventDefault();
        if (isVerifiedEmail) {
          handleResendEmail();
        } else {
          handleSignIn();
        }
      }
    };
    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signInFormData, isVerifiedEmail]);

  return (
    <React.Fragment>
      <div className='h-full w-full flex flex-col items-center justify-center gap-3'>
        {/* LOGO */}
        <div className='w-[150px] cursor-pointer'>
          {isVerifiedEmail || isRequestSend ? (
            <Link to='/'>
              <img src={assets.logo} className='object-fit' alt='Rylyf' />
            </Link>
          ) : (
            <img src={assets.logo} className='object-fit' alt='Rylyf' />
          )}
        </div>

        {/* Form Section */}
        <section className='w-full max-w-md rounded-xl'>
          <div className='container flex items-center justify-center px-6 mx-auto'>
            <div className='w-full max-w-md'>
              {/* LOGO Name */}
              <div className='flex flex-col items-center justify-center'>
                <h1 className='text-3xl font-normal text-gray-800 capitalize sm:text-3xl'>
                  {isVerifiedEmail ? 'Resend Email' : isRequestSend ? 'Resend Email' : 'Sign In'}
                </h1>
              </div>

              <Alert
                color='danger'
                text={error ? error : authError ? authError : ''}
                showAlert={isShowAlert}
                setShowAlert={setIsShowAlert}
              />

              {/* Login Fields */}
              {isVerifiedEmail ? (
                <div className='relative flex flex-col items-center mt-4 gap-4'>
                  <TextField
                    name={'email'}
                    value={signInFormData?.email}
                    type='email'
                    placeholder='Email'
                    icon={true}
                    onChange={handleOnChange}
                    error={
                      emptyData?.email || emptyData?.isEmailValid || isShowAlert ? true : false
                    }
                    errorMsg={
                      (emptyData?.email && 'Email is required') ||
                      (emptyData?.isEmailValid && 'Email address is not valid')
                    }
                  />
                </div>
              ) : isRequestSend ? (
                <div className='flex flex-col text-center items-center justify-center mt-4 gap-4'>
                  <p className='text-gray-700 mr-2 md:text-sm text-[14px]'>
                    A new verification link has been sent to your email. Please check your email.
                  </p>
                </div>
              ) : (
                <div className='relative flex flex-col items-center mt-4 gap-4'>
                  <TextField
                    name={'email'}
                    value={signInFormData?.email}
                    type='email'
                    placeholder='Email'
                    icon={true}
                    onChange={handleOnChange}
                    error={
                      emptyData?.email || emptyData?.isEmailValid || isShowAlert ? true : false
                    }
                    errorMsg={
                      (emptyData?.email && 'Email is required') ||
                      (emptyData?.isEmailValid && 'Email address is not valid')
                    }
                  />
                  <TextField
                    name={'password'}
                    value={signInFormData?.password}
                    type='password'
                    placeholder='Password'
                    icon={true}
                    onChange={handleOnChange}
                    error={emptyData?.password || isShowAlert ? true : false}
                    errorMsg={emptyData?.password && 'Password is required'}
                  />
                </div>
              )}

              {/* Button Section */}
              {isVerifiedEmail ? (
                <div className='mt-4'>
                  <Button
                    onClick={handleResendEmail}
                    variant='primary'
                    fullWidth
                    loading={authLoading}
                  >
                    Resend Email
                  </Button>
                </div>
              ) : isRequestSend ? (
                <div className='mt-4'>
                  <Button
                    variant='primary'
                    fullWidth
                    onClick={() => {
                      setIsVerifiedEmail(false);
                      setIsRequestSend(false);
                      setIsShowAlert(false);
                      setIsResendBtn(false);
                    }}
                  >
                    Done
                  </Button>
                </div>
              ) : (
                <div className='mt-4 flex flex-col gap-2'>
                  <Button onClick={handleSignIn} variant='primary' fullWidth loading={loading}>
                    Sign in
                  </Button>
                  {isResendBtn && (
                    <Button
                      onClick={() => {
                        setIsVerifiedEmail(true);
                        setIsShowAlert(false);
                      }}
                      variant='secondary'
                      fullWidth
                      disabled={loading}
                    >
                      Resend Email
                    </Button>
                  )}

                  <div className='mt-1 flex flex-col gap-2 text-center '>
                    <Link to='/forgot-password' className='text-sm text-blue-500 hover:underline'>
                      Forgot your password?
                    </Link>
                    <Link to='/sign-up' className='text-sm text-blue-500 hover:underline'>
                      Don’t have an account yet? Sign up
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </React.Fragment>
  );
};

export default LoginForm;
